
import { useState, useCallback, useMemo } from 'react';

// Helper function to format date for filtering without timezone conversion
const formatDateForFilter = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export interface FilterValues {
  pic: string[];
  language: string[];
  channel: string[];
  platform: string[];
  campaign_bu: string[];
  expense: string[];
  type: string[];
  notes: string;
  // Date range filters
  postingDateFrom: Date | null;
  postingDateTo: Date | null;
  // Null/empty filters
  picNullEmpty: boolean;
  languageNullEmpty: boolean;
  channelNullEmpty: boolean;
  platformNullEmpty: boolean;
  campaign_buNullEmpty: boolean;
  expenseNullEmpty: boolean;
  typeNullEmpty: boolean;
  notesNullEmpty: boolean;
}

const initialFilters: FilterValues = {
  pic: [],
  language: [],
  channel: [],
  platform: [],
  campaign_bu: [],
  expense: [],
  type: [],
  notes: '',
  postingDateFrom: null,
  postingDateTo: null,
  picNullEmpty: false,
  languageNullEmpty: false,
  channelNullEmpty: false,
  platformNullEmpty: false,
  campaign_buNullEmpty: false,
  expenseNullEmpty: false,
  typeNullEmpty: false,
  notesNullEmpty: false,
};

export const useKolPostingLinksFilters = () => {
  const [filters, setFilters] = useState<FilterValues>(initialFilters);
  const [isLoading, setIsLoading] = useState(false);

  const updateFilter = useCallback((field: keyof FilterValues, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.pic.length > 0 ||
      filters.language.length > 0 ||
      filters.channel.length > 0 ||
      filters.platform.length > 0 ||
      filters.campaign_bu.length > 0 ||
      filters.expense.length > 0 ||
      filters.type.length > 0 ||
      filters.notes.trim() !== '' ||
      filters.postingDateFrom !== null ||
      filters.postingDateTo !== null ||
      filters.picNullEmpty ||
      filters.languageNullEmpty ||
      filters.channelNullEmpty ||
      filters.platformNullEmpty ||
      filters.campaign_buNullEmpty ||
      filters.expenseNullEmpty ||
      filters.typeNullEmpty ||
      filters.notesNullEmpty
    );
  }, [filters]);

  // Build filter query for Supabase with improved logic
  const applyFilters = useCallback((baseQuery: any) => {
    let query = baseQuery;

    // Handle array filters for pic field
    if (filters.pic.length > 0) {
      const picConditions = filters.pic.map(pic => `pic.cs.["${pic}"]`);
      query = query.or(picConditions.join(','));
    }

    // Handle simple string filters
    if (filters.language.length > 0) {
      query = query.in('language', filters.language);
    }

    if (filters.channel.length > 0) {
      query = query.in('channel', filters.channel);
    }

    if (filters.platform.length > 0) {
      query = query.in('platform', filters.platform);
    }

    if (filters.type.length > 0) {
      query = query.in('type', filters.type);
    }

    // Handle array filters for campaign_bu field
    if (filters.campaign_bu.length > 0) {
      const campaignBuConditions = filters.campaign_bu.map(bu => `campaign_bu.cs.["${bu}"]`);
      query = query.or(campaignBuConditions.join(','));
    }

    // Handle date range filters
    if (filters.postingDateFrom) {
      // Use timezone-neutral date formatting to avoid timezone conversion issues
      const fromDate = formatDateForFilter(filters.postingDateFrom);
      query = query.gte('posting_date', fromDate);
    }

    if (filters.postingDateTo) {
      // Use timezone-neutral date formatting to avoid timezone conversion issues
      const toDate = formatDateForFilter(filters.postingDateTo);
      query = query.lte('posting_date', toDate);
    }

    // Handle expense range filters
    if (filters.expense.length > 0) {
      const expenseConditions = filters.expense.map(range => {
        switch (range) {
          case 'zero':
            return 'expense.eq.0';
          case '1-100':
            return 'and(expense.gte.1,expense.lte.100)';
          case '101-500':
            return 'and(expense.gte.101,expense.lte.500)';
          case '501-1000':
            return 'and(expense.gte.501,expense.lte.1000)';
          case '1000+':
            return 'expense.gt.1000';
          default:
            return null;
        }
      }).filter(Boolean);
      
      if (expenseConditions.length > 0) {
        query = query.or(expenseConditions.join(','));
      }
    }

    // Handle notes text filter
    if (filters.notes.trim() !== '') {
      query = query.ilike('notes', `%${filters.notes.trim()}%`);
    }

    // Handle null/empty filters
    if (filters.picNullEmpty) {
      query = query.or('pic.is.null,pic.eq.[]');
    }

    if (filters.languageNullEmpty) {
      query = query.or('language.is.null,language.eq.');
    }

    if (filters.channelNullEmpty) {
      query = query.or('channel.is.null,channel.eq.');
    }

    if (filters.platformNullEmpty) {
      query = query.or('platform.is.null,platform.eq.');
    }

    if (filters.campaign_buNullEmpty) {
      query = query.or('campaign_bu.is.null,campaign_bu.eq.[]');
    }

    if (filters.expenseNullEmpty) {
      query = query.or('expense.is.null');
    }

    if (filters.typeNullEmpty) {
      query = query.or('type.is.null,type.eq.');
    }

    if (filters.notesNullEmpty) {
      query = query.or('notes.is.null,notes.eq.');
    }

    return query;
  }, [filters]);

  return {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    applyFilters,
    isLoading,
    setIsLoading
  };
};
